import { useState, useEffect } from 'react';
import { 
  Database, 
  Upload, 
  RefreshCw, 
  ArrowLeft,
  Info,
  Image,
  FileText
} from 'lucide-react';
import { Link } from 'react-router-dom';
import VercelBlobUpload from '../components/VercelBlobUpload';
import VercelBlobCRUD from '../components/VercelBlobCRUD';
import vercelBlobService from '../services/vercelBlobService';

const VercelBlobManager = () => {
  const [storeInfo, setStoreInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('browse');

  useEffect(() => {
    loadStoreInfo();
  }, []);

  const loadStoreInfo = async () => {
    setLoading(true);
    try {
      const info = await vercelBlobService.getStoreInfo();
      if (info.success) {
        setStoreInfo(info);
      }
    } catch (error) {
      console.error('Error loading store info:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <Link 
                to="/vercel-super-admin" 
                className="flex items-center text-gray-600 hover:text-gray-900 mr-4"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back to Admin
              </Link>
              <Database className="w-8 h-8 text-blue-600 mr-3" />
              <div>
                <h1 className="text-xl font-bold text-gray-900">Vercel Blob Manager</h1>
                <p className="text-sm text-gray-600">
                  Store: {vercelBlobService.storeId} | Path: {vercelBlobService.storePath}
                </p>
              </div>
            </div>
            <button
              onClick={loadStoreInfo}
              disabled={loading}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Store Information */}
      {storeInfo && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="flex items-center">
                <Info className="w-8 h-8 text-blue-600 mr-3" />
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Files</p>
                  <p className="text-2xl font-bold text-gray-900">{storeInfo.totalFiles}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="flex items-center">
                <Image className="w-8 h-8 text-green-600 mr-3" />
                <div>
                  <p className="text-sm font-medium text-gray-600">Images</p>
                  <p className="text-2xl font-bold text-gray-900">{storeInfo.imageFiles}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="flex items-center">
                <Database className="w-8 h-8 text-purple-600 mr-3" />
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Size</p>
                  <p className="text-2xl font-bold text-gray-900">{formatFileSize(storeInfo.totalSize)}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="flex items-center">
                <FileText className="w-8 h-8 text-orange-600 mr-3" />
                <div>
                  <p className="text-sm font-medium text-gray-600">File Types</p>
                  <p className="text-2xl font-bold text-gray-900">{Object.keys(storeInfo.fileTypes).length}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Navigation Tabs */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('browse')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'browse'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Database className="w-4 h-4 inline mr-2" />
              Browse & Manage Files
            </button>
            <button
              onClick={() => setActiveTab('upload')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'upload'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Upload className="w-4 h-4 inline mr-2" />
              Upload Files
            </button>
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'browse' && (
          <div className="bg-white rounded-lg shadow-sm">
            <VercelBlobCRUD />
          </div>
        )}

        {activeTab === 'upload' && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <VercelBlobUpload
              onUploadSuccess={(result) => {
                console.log('✅ Upload successful:', result);
                loadStoreInfo();
              }}
              onUploadError={(error) => {
                console.error('❌ Upload failed:', error);
              }}
              allowMultiple={true}
              acceptedTypes="*/*"
              showMetadataForm={true}
            />
          </div>
        )}
      </div>

      {/* Store Details */}
      {storeInfo && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Store Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Store Information</h4>
                <dl className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <dt className="text-gray-600">Store ID:</dt>
                    <dd className="text-gray-900 font-mono">{storeInfo.storeId}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-600">Store Path:</dt>
                    <dd className="text-gray-900 font-mono">{storeInfo.storePath}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-600">Total Files:</dt>
                    <dd className="text-gray-900">{storeInfo.totalFiles}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-600">Total Size:</dt>
                    <dd className="text-gray-900">{formatFileSize(storeInfo.totalSize)}</dd>
                  </div>
                </dl>
              </div>
              
              <div>
                <h4 className="font-medium text-gray-900 mb-2">File Types</h4>
                <div className="space-y-1 text-sm">
                  {Object.entries(storeInfo.fileTypes).map(([type, count]) => (
                    <div key={type} className="flex justify-between">
                      <span className="text-gray-600">.{type}:</span>
                      <span className="text-gray-900">{count} files</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VercelBlobManager;
